import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import {
    AccessLevel,
    Application,
    ControlRegexValidator,
    EntitlementSet,
    DataContainer,
    ProductDefinition,
    iApplyFormControl,
    isContainerControl,
    Qualifier,
    isControlWithValidators,
    NavigationStyle,
    FormPage,
    GroupControl,
    FormPageType,
    isGroupControl,
    Organisation,
} from "app/shared/model";
import { ApplicationsServiceProxy } from "app/shared/services/application-service.proxy";
import { FormServiceProxy } from "app/shared/services/form-service.proxy";
import {
    ApplicationState,
    FormAccessEntitlements,
    GetEntitlementsInput,
    GetProductEntitlementsResponse,
    GetProductMenuResponse,
    GetProductResponse,
    IApplicationState,
    SurveyResultResponse,
    UploadFileResponse,
    Validator,
    ValidatorListResult
} from "app/shared/services/model/form-service.model";
import {
    BehaviorSubject,
    Observable,
    catchError,
    concatMap,
    forkJoin,
    of,
    switchMap,
    tap,
    throwError
} from "rxjs";
import {
    CreateApplicationResponse,
    GetApplicationResponse,
    ISaveApplicationInput,
    SaveApplicationBySearchControlResponse,
    SaveApplicationResponse,
    SubmitApplicationResponse,
    ILastVisitedPage,
    LastVisitedPage
} from "../../shared/services/model/application-service.model";
import { CommunicatorService } from "app/shared/services/communicator.service";
import { DataSharingService } from "app/shared/services/data-sharing.service";
import { EXTERNAL_DATASET_UPDATED_MESSAGE, GetExternalDataResponse } from "app/shared/services/model/external-data-service.model";
import { environment } from "environments/environment";
import { ExternalDataService } from "./external-data/external-data.service";
import { VisibilityService } from "../visibility/visibility.service";
import { ControlVisibilityService } from "../visibility/control-visibility.service";
import { ControlVisibilityRunService } from "../visibility/control-visibility-run.service";
import { ApplicationHelperService } from "./application-helper.service";
import { hasValue, toJson } from "app/shared";
import { MessageModel } from "app/shared/services/model/communicatorModel";
import { GlobalService } from "app/shared/services/global.service";
import { VaultProductsFilter, GetVaultProductResult, ProductSearchResult } from "app/shared/services/model/form-vault.model";
import { DateTime } from "luxon";
import { ApiException } from "app/shared/services/service-functions";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormNavigationItem } from './components/navigation';
import { OrganisationServiceProxy } from 'app/shared/services/organisation-service.proxy';
import { GetOrganisationResponse } from 'app/shared/services/model/organisation-service.model';
import _ from 'lodash';

@Injectable()
export class FormService
{
    private readonly _document: any = inject(DOCUMENT);
    private readonly _router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    private readonly _applicationHelperService = inject(ApplicationHelperService);
    private readonly _applicationServiceProxy = inject(ApplicationsServiceProxy);
    private readonly _communicatorService: CommunicatorService = inject(CommunicatorService);
    private readonly _controlVisibilityRunService = inject(ControlVisibilityRunService);
    private readonly _controlVisibilityService = inject(ControlVisibilityService);
    private readonly _dataSharingService: DataSharingService = inject(DataSharingService);
    private readonly _externalDataService = inject(ExternalDataService);
    private readonly _formServiceProxy = inject(FormServiceProxy);
    private readonly _orgServiceProxy = inject(OrganisationServiceProxy);
    private readonly _visibilityService = inject(VisibilityService);

    private readonly _globalService = inject(GlobalService);

    // ReplaySubject or BehaviorSubject
    private readonly _application: BehaviorSubject<Application | null> = new BehaviorSubject<Application| null>(null);
    private readonly _product: BehaviorSubject<ProductDefinition | null> = new BehaviorSubject<ProductDefinition | null>(null);
    private readonly _organisation: BehaviorSubject<Organisation | null> = new BehaviorSubject<Organisation | null>(null);
    private readonly _externalPopulationFields: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);
    private readonly _entitlements: BehaviorSubject<FormAccessEntitlements | null> = new BehaviorSubject<FormAccessEntitlements | null>(null);
    private readonly _menuItems: BehaviorSubject<FormNavigationItem[] | null> = new BehaviorSubject<FormNavigationItem[] | null>(null);
    private readonly _applicationData: BehaviorSubject<DataContainer | null> = new BehaviorSubject<DataContainer | null>(null);
    private readonly _lastVisitedPage: BehaviorSubject<ILastVisitedPage | null> = new BehaviorSubject<ILastVisitedPage | null>(null);
    private readonly _validators: BehaviorSubject<Validator[] | null> = new BehaviorSubject<Validator[] | null>(null);

    currentState: ApplicationState = new ApplicationState();
    validatorSyncVersion: number = 0;

    fileContentsHandlerUrl: string = '';
    getApplicationError = signal<ApiException | undefined>(undefined);

    constructor() {
        this.fileContentsHandlerUrl =
            (environment.config?.remoteServiceBaseUrl ?? '') +
            (environment.config?.fileUploadEndpoint ?? '');

        this.setState();
        this.product$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(product => this.setProductClassOverrides(product));
    }

    get application$(): Observable<Application | null> {
        return this._application.asObservable();
    }

    get product$(): Observable<ProductDefinition | null> {
        return this._product.asObservable();
    }

    get productSnapshot(): ProductDefinition | null {
        return this._product.getValue();
    }

    get externalPopulationFields$(): Observable<string[] | null> {
        return this._externalPopulationFields.asObservable();
    }

    get entitlements$(): Observable<FormAccessEntitlements | null> {
        return this._entitlements.asObservable();
    }

    get menuItems$(): Observable<FormNavigationItem[] | null> {
        return this._menuItems.asObservable();
    }

    get applicationData$(): Observable<DataContainer | null> {
        return this._applicationData.asObservable();
    }

    get applicationDataSnapshot(): DataContainer | null {
        return this._applicationData.getValue();
    }

    get lastVisitedPage$(): Observable<ILastVisitedPage | null> {
        return this._lastVisitedPage.asObservable();
    }

    get validators$(): Observable<Validator[] | null> {
        return this._validators.asObservable();
    }

    get organisation$(): Observable<Organisation | null> {
        return this._organisation.asObservable();
    }

    getCurrentProduct = () => this._product.getValue();

    setProductProperty(propertyName: string, value: any) {
        let updatedProduct = this._product.getValue();
        updatedProduct[propertyName] = value;
        this._product.next(updatedProduct);
    }

    destroyApplicationSession() {
        this.setState();
        this._application.next(null);
        this._product.next(null);
        this._externalPopulationFields.next(null);
        this._entitlements.next(null);
        this._menuItems.next(null);
        this._applicationData.next(null);
        this._lastVisitedPage.next(null);
        this._communicatorService.setApplication(null);
    }

    public setState(state?: IApplicationState) {
        this.currentState = new ApplicationState(state);
    }

    onApplicationSubmitted(result: SubmitApplicationResponse): Observable<any> {
        if (!result.success || !result.valid) {
            return of(false);
        }
        // const pageId = this._currentMenuItem //+route.paramMap.get('pageId');
        let formVersion: number = result.application.useFormVersion ? result.application.formVersion : null;

        let state = <IApplicationState>{
            formId: result.application.formId,
            formVersion: formVersion,
            applicationId: result.application.id,
            applicationIdDisplay: result?.application?.applicationIdDisplay,
            offlineApplicationId: null,
            applicationVersion: result.application.version,
            workflowState: result.application.applicationState,
            workflowStateId: null,
            created: result?.application?.created,
            pageId: 0,
            subPageId: null,
            organisationId: null,
            readonly: false
        };

        // TODO?: Should we let resolveApplicationDependencies() handle this next step?
        this._application.next(result.application);
        let data = result.application.data;
        data['$$qualifier'] = new Qualifier();
        this._communicatorService.setApplication(result.application);
        this._applicationData.next(this._communicatorService.getApplicationData());
        return this.resolveApplicationDependencies(state);
    }

    /**
     * RESOLVER for all data related to the editing of an application (product/menu/app/entitlements etc.)
     */
    resolveApplicationDependencies(state: IApplicationState, fetchNew: boolean = false): Observable<any> {
        if (!fetchNew && this.currentState?.isCurrentState(state)) {
            return of(true);
        }

        let isCurrentApplication = !fetchNew && (this.currentState?.isCurrentApplication(state.applicationId, state.applicationVersion, true) ?? false);

        this._visibilityService.unsubscribeAll();

        let getApp$ = isCurrentApplication
            ? of(new GetApplicationResponse({ success: true, application: this._application.getValue() }))
            : this.getApplication(state.applicationId);

        return getApp$.pipe(

            // switchMap(): Get the product once we have the application and validate the ID
            switchMap((response: GetApplicationResponse) => {
                // We wait until the application has been returned because the current state Form ID must match the Applications Form ID!
                if (response.application.formId != state.formId) {
                    throw new Error(`Form ID mismatch: ${response.application.formId} != ${state.formId}`);
                }

                let requiredVersion = response.application.useFormVersion ? response.application.formVersion : null;
                let isCurrentProduct = !fetchNew && (this.currentState?.isCurrentProduct(state.formId, state.formVersion) ?? false);

                let getProduct$ = isCurrentProduct
                    ? of((new GetProductResponse).init({
                        success: true,
                        metadata: this._product.getValue(),
                        paymentData: [],
                        externalPopulatedFields: this._externalPopulationFields.getValue()
                    }))
                    : this.getProduct(state.formId, requiredVersion, 0 /* pageId */, state.applicationId);

                return forkJoin({
                    product: getProduct$,
                    appResp: of(response)
                });
            }),

            // switchMap(): Once we hacve the product, get the Validators, Menu, Entitlements etc.
            switchMap(data => {
                let version = state.formVersion || data.product.metadata.version;
                let getValidators$ = this.getValidators();
                let getMenu$ = this.getProductMenu(state, data.product.metadata);

                let getEntitlements$ = this.getEntitlements({
                    formId: state.formId,
                    formVersion: version,
                    applicationId: state.applicationId,
                    pageId: 0  /* state.pageId */
                });
                let getSurvey$ = this.getSurveyResult(state.formId, version, state.applicationId, data.appResp.application.data);

                return forkJoin({
                    menu: getMenu$,
                    entitlements: getEntitlements$,
                    product: of(data.product),
                    app: of(data.appResp),
                    survey: getSurvey$,
                    validators: getValidators$,
                })
            }),

            // tap(): Once we have everything, we can setup the form...
            tap({
                next: (data) => {
                    const product: ProductDefinition = data.product.metadata;
                    const application = data.app.application;
                    const menu = data.menu.menu;

                    if (!product) {
                        return;
                    }

                    if (!state.pageId && this._globalService.isOnline()) {
                        this.getLastVisitedPage(product, application.id);
                    }

                    this.updateControlPageInfo(product);
                    this.updateControlEntitlements(product, data.entitlements.controlPermissions, state.readonly || data.entitlements.baseAccess !== AccessLevel.Write);

                    if (product.pages && data.validators.validators) {
                        // const isFullScreen = product.navigationStyle == NavigationStyle.FullScreen;
                        product.pages.forEach((page: FormPage) => {
                            this.updateControlValidators(page.controls);
                            // if (isUserFormPage(page) && isFullScreen) {
                            //     page.subPage = true;
                            // }
                        });
                    }

                    this._dataSharingService.application = application;

                    const controlList: iApplyFormControl[] = product.pages.map((page) => page.controls).flat();
                    const allControls: iApplyFormControl[] = this._applicationHelperService.flattenControlList(controlList);

                    this._dataSharingService.surveyResult = data.survey ? data.survey.result : null;
                    this._dataSharingService.product = product;
                    this._dataSharingService.formId = product.productId;
                    this._dataSharingService.applicationId = application.id;
                    this._dataSharingService.controls = controlList;

                    const pageId = state.pageId > 0
                        ? state.pageId
                        : this.currentState.pageId > 0
                            ? this.currentState.pageId
                            : menu.find(item => item.accessLevel > AccessLevel.NoAccess)?.pageId || 0;
                    const currentPageIndex = product.pages.findIndex(p => p.pageId === pageId);
                    this._dataSharingService.currentPageIndex = currentPageIndex < 0 ? 0 : currentPageIndex;

                    this._controlVisibilityRunService.refreshControlsVisibility(allControls, this._communicatorService.getApplicationData());
                    this._controlVisibilityService.createVisibilityWatch(
                        allControls,
                        this._communicatorService.getApplicationData(),
                        new Qualifier(),
                        this._visibilityService,
                        this._dataSharingService.surveyResult,
                        this._dataSharingService.controls);

                    // if (product.pages && application.data) {
                    //     product.pages.forEach(page => {
                    //         this._controlVisibilityRunService.refreshControlsVisibility(page.controls, this._communicatorService.getApplicationData());
                    //         this._controlVisibilityService.createVisibilityWatch(
                    //             page.controls,
                    //             this._communicatorService.getApplicationData(),
                    //             new Qualifier(),
                    //             this._visibilityService,
                    //             this._dataSharingService.surveyResult,
                    //             this._dataSharingService.controls);
                    //     });
                    // }
                }
            }),
            concatMap((data) => {
                if (hasValue(data.product.externalPopulatedFields)) {
                    return this.getExternalData(data.product.metadata, data.app.application, state.pageId)
                                    .pipe(
                                        catchError(() => of(null)),
                                        switchMap(() => of(data))
                                    );
                }

                return of(data);
            }),
            tap({
                next: (data) => {
                    let newState = new ApplicationState(state);
                    newState.formVersion = data.app.application.useFormVersion ? data.app.application.formVersion : state.formVersion;
                    newState.applicationVersion = data.app?.application?.version;
                    newState.workflowState = data.app?.application?.applicationState;
                    newState.created = data.app?.application?.created;
                    newState.applicationIdDisplay = data.app?.application?.applicationIdDisplay;
                    newState.offlineApplicationId = data.app?.application?.offlineApplicationId;
                    newState.organisationId = data.app?.application?.organisationId;
                    // newState.organisationName = data.app?.application?.organisationName;
                    newState.readonly = data.app?.application?.offlineEventType == "submit";
                    this.setState(newState)
                }
            }),
            catchError(error => {
                if (this.getApplicationError()) {
                    return of(null);
                }
                return throwError(() => error);
            }),
        );
    }

    getExternalData(product: ProductDefinition, application: Application, pageId: number): Observable<GetExternalDataResponse> {
        let appDataClone = application?.data ? _.cloneDeep(application.data) : {};

        // TODO: This ERASES application data when the server saves the updated app!!!! we need to do this a different way.
        // const externalFields = this._externalPopulationFields.getValue();
        // if (Array.isArray(externalFields)) {
        //     externalFields.forEach(field => {
        //         _.unset(appDataClone, field);
        //     });
        // }

        return this._externalDataService.getExternalData(product.productId, product.version, pageId, application.id, toJson(appDataClone)).pipe(
            tap((response: GetExternalDataResponse) => {
                if (response?.Success && response?.Result) {
                    const appData = response.Result;

                    let container = this._applicationData.getValue();
                    for (let key in appData) {
                        if (Object.hasOwn(appData, key)) {
                            this._applicationHelperService.setFieldValue(container, key, appData[key]);
                        }
                    }

                    this._applicationData.next(container);

                    if (container?.returnUrl) {
                        product.closeNavigationAddress = container.returnUrl;
                    }

                    // trigger message EXTERNAL_DATASET_UPDATED_MESSAGE so repeater can listen to it to get updated if needed
                    this._communicatorService.sendMessage(new MessageModel(EXTERNAL_DATASET_UPDATED_MESSAGE, null));

                    // Re-trigger calculations after data pop has populated hidden fields.
                    this._controlVisibilityRunService._doCalculationVisibilityRun(null, true);
                }
                else if (!response?.Success && response?.Message) {
                    console.error(response.Message);
                }
            }),
            catchError(this.logErrorOnly),
        );
    }

    getProduct(formId: string, versionNumber: number | null, pageId: number, applicationId: string): Observable<GetProductResponse>
    {
        let prodResp$ = this._formServiceProxy.getProduct(formId, versionNumber, pageId, applicationId);
        let product = this._product.getValue();

        return prodResp$
            .pipe(
                switchMap(async (result: GetProductResponse) => {
                    if (product?.productId != result.metadata.productId || product?.version != result.metadata.version) {
                        // Set Form Metadata observable.
                        this._product.next(result.metadata);
                        // result.paymentData
                        this._externalPopulationFields.next(result.externalPopulatedFields);
                        this._dataSharingService.externalPopulatedFields = result.externalPopulatedFields || [];
                        // update the current state
                        this.currentState.formId = result.metadata.productId;
                        this.currentState.formVersion = result.metadata.version;
                        this.currentState.organisationId = result.metadata.organisationId;
                        this.loadOrganisation(result.metadata.organisationId);
                    }

                    return result;
                }),
                catchError(this.navigateOnError)
            );
    }

    loadOrganisation(orgId: string): void {
        this._orgServiceProxy.getOrganisation(orgId)
        .subscribe({
            next: (response: GetOrganisationResponse) => {
                if (response.success) {
                    this._organisation.next(response.organisation);
                } else {
                    this._organisation.next(null);
                }
            },
            error: (err) => {
                this._organisation.next(null);
            }
        });
    }

    findVaultProducts(productIdOrFilter: string | VaultProductsFilter): Observable<ProductSearchResult> {
        let filter = new VaultProductsFilter();
        if (!(productIdOrFilter instanceof VaultProductsFilter)) {
            filter.filter.FormIds = [productIdOrFilter];
            filter.pagination.pageSize = 1000;
        } else {
            filter = productIdOrFilter;
        }

        return this._formServiceProxy.findVaultProducts(filter);
    }

    getVaultProduct(productId: string): Observable<GetVaultProductResult> {
        return this._formServiceProxy.getVaultProduct(productId);
    }

    createApplicationOnline(formId: string, saveApplication: boolean, formVersion: number = null): Observable<CreateApplicationResponse> {
        return this._applicationServiceProxy.createApplication({ formId: formId, saveApplication: saveApplication, formVersion: formVersion });
    }

    createApplication(formId: string): Observable<CreateApplicationResponse> {
        return this.createApplicationOnline(formId, true, null).pipe(
            tap((result: CreateApplicationResponse) => {
                let data: DataContainer = {
                    $$qualifier: new Qualifier()
                };

                this._application.next(result.application);
                data = { ...data, ...result.application.data };
                this._communicatorService.setApplication(result.application);
                this._applicationData.next(this._communicatorService.getApplicationData());
                this.currentState.applicationVersion = result.application.version;
                return result;
            }),
            catchError(this.navigateOnError)
        );
    }

    getApplication(applicationId: string): Observable<GetApplicationResponse>
    {
        this.getApplicationError.set(null);
        return this._applicationServiceProxy.getApplication(applicationId, null)
            .pipe(
                tap((result: GetApplicationResponse) => {
                    if (result.success && result.application) {
                        this._application.next(result.application);
                        this._communicatorService.setApplication(result.application);
                        this._applicationData.next(this._communicatorService.getApplicationData());
                        this.currentState.applicationVersion = result.application.version;
                    } else {
                        this.destroyApplicationSession();
                    }

                    return result;
                }),
                catchError((error) => {
                    const apiError = <ApiException>error;
                    if (apiError?.status == 400) {
                        // store the error for future usage in application-editor
                        this.getApplicationError.set(apiError);
                        this.destroyApplicationSession();
                        return throwError(() => apiError);
                    }

                    return this.navigateOnError(error);
                })
            );
    }

    getSurveyResult(formId: string,
        formVersion: number | null,
        applicationId: string,
        applicationData: any): Observable<SurveyResultResponse> {
        return this._applicationServiceProxy.getSurveyResult(
            {
                formId: formId,
                formVersion: formVersion,
                applicationId: applicationId,
                applicationData: typeof applicationData === "object" ? toJson(applicationData) : applicationData
            });
    }

    getEntitlementsOnline(formId: string, formVersion: number | null, pageId: number | null, applicationId: string) {
        return this._formServiceProxy.getProductEntitlements(formId, formVersion, pageId, applicationId);
    }

    getEntitlements(input: GetEntitlementsInput): Observable<FormAccessEntitlements> {
        // {"formId":"6493b78a4b14d5fd59933206","formVersion":null,"pageId":1,"applicationId":"64c09e07914b951496477b9b"}
        return this.getEntitlementsOnline(input.formId, input.formVersion, input.pageId, input.applicationId)
            .pipe(
                switchMap(async (result: GetProductEntitlementsResponse) => {
                    this._entitlements.next(result.accessRights);
                    return result.accessRights;
                }),
                catchError((error) => {
                    console.error(error);
                    this._router.navigateByUrl('/error/403');
                    return throwError(() => new Error(error));
                })
            );
    }

    getProductMenu(state: IApplicationState, metadata?: ProductDefinition): Observable<GetProductMenuResponse>
    {
        if (this._menuItems?.value && this.currentState.isCurrentState(state)) {
            return of((new GetProductMenuResponse()).init({
                success: true,
                menu: this._menuItems.value
            }));
        }

        return this._formServiceProxy
            .getProductMenu(state.formId, state.applicationId, state.formVersion)
            .pipe(
                tap((result: GetProductMenuResponse) =>
                {
                    let menu = result.menu;
                    if (metadata) {
                        menu.forEach((menuItem: FormNavigationItem) => menuItem.pageTabIndex = metadata.pages.findIndex(p => p.pageId === menuItem.pageId));
                    }

                    // map menu items to page indexes.
                    menu.forEach((menuItem: FormNavigationItem, index: number) =>
                    {
                        this.mapSubMenuItemsFromPages(menuItem, metadata);

                        // Add all top-level control groups to the navigation menus as Sub Menu Items...
                        menuItem.sortIndex = index;
                        if (menuItem.subItems?.length) {
                            menuItem.subItems.forEach((child: FormNavigationItem, childIndex: number) => child.sortIndex = childIndex);
                        }
                    });

                    this._menuItems.next(menu);
                }),
                catchError(this.navigateOnError)
            );
    }

    private mapSubMenuItemsFromPages(menuItem: FormNavigationItem, metadata?: ProductDefinition) {
        let fullPageNavigation = metadata?.navigationStyle == NavigationStyle.FullScreen;
        if (!fullPageNavigation || menuItem.subItems.length > 0 || !metadata) {
            // already have sub-menu-items
            return;
        }

        let page = metadata.pages.find(p => p.pageId == menuItem.pageId);
        if (page) {
            // loop through all top-level control groups.
            let groups = page.controls.filter(c => isGroupControl(c));
            groups.forEach((g: GroupControl) => {
                let childMenuItem = (new FormNavigationItem()).init({
                    title: g.label,
                    pageId: menuItem.pageId,
                    controlId: g.id,
                    submitsApplication: false,
                    visibilityRule: g.visibilityRule,
                    accessLevel: AccessLevel.Read, // g.access,
                    subItems: [],
                    pageType: FormPageType.User,
                    // progress: FormNavigationProgress = new FormNavigationProgress(0);
                    // pageTabIndex: number = 0; // Used to bind a navigation item to a page tab.
                    // sortIndex: number = 0; // U
                });

                menuItem.subItems.push(childMenuItem);
            });
        }
    }

    getValidators(): Observable<ValidatorListResult> {
        const localVersion = 0;
        return this._formServiceProxy.getValidatorList(localVersion)
            .pipe(
                switchMap(async (result: ValidatorListResult) => {
                    this._validators.next(result.validators);
                    this.validatorSyncVersion = result.validatorSyncVersion;

                    return result;
                }),
                catchError(this.navigateOnError)
            );
    }

    getValidator(validatorId: string): Validator {
        return this._validators.getValue()?.find(validator => validator.id === validatorId);
    }

    getLastVisitedPage(product: ProductDefinition, applicationId: string) {
        if (product.autoOpenLastVisitedPage === true) {
            this._applicationServiceProxy.getLastVisitedPage(applicationId)
                .subscribe({
                    next: (response: LastVisitedPage) => {
                        this._lastVisitedPage.next(response);
                    },
                    error: console.error
                });
        }
    }

    mapValidators(control: iApplyFormControl) {
        if (isControlWithValidators(control)) {
            control.validators?.forEach((ref: ControlRegexValidator) => {
                ref.validator ??= this.getValidator(ref.validatorId);
            });
        }
    }

    updateContainerData(container: DataContainer, key: string, newValue: any) {
        // container = container || this._applicationData.getValue();
        this._applicationHelperService.setFieldValue(container, key, newValue);
    }

    updateControlPageInfo(product: ProductDefinition) {
        for (let page of product.pages) {
            page.pageIndex = product.pages.findIndex(p => p.pageId === page.pageId);
            this.updateControlPageId(page.controls, page.pageId, page.pageIndex);
        }
    }

    updateControlValidators(controls: iApplyFormControl[]) {
        for (let control of controls) {
            if (isContainerControl(control)) {
                this.updateControlValidators(control.controls);
            } else {
                this.mapValidators(control);
            }
        }
    }

    updateControlPageId(controls: iApplyFormControl[], pageId: number, pageIndex: number) {
        for (let control of controls) {
            control.pageId = pageId;
            control.pageIndex = pageIndex;

            if (isContainerControl(control)) {
                this.updateControlPageId(control.controls, pageId, pageIndex);
            }
        }
    }

    saveApplication(data: ISaveApplicationInput): Observable<SaveApplicationResponse> {
        return this._applicationServiceProxy.saveApplication(data)
            .pipe(
                tap((result: SaveApplicationResponse) => {
                    // console.log('FormService.saveApplication()', result);
                    if (result.success && result.application) {
                        // Update the application version from the save response.
                        this.currentState.applicationVersion = result.application.version;
                    }
                    return result;
                }),
                // catchError(this.navigateOnError)
            );
    }

    saveCurrentApplication(): Observable<SaveApplicationResponse> {
        if (!this.currentState) {
            return of(new SaveApplicationResponse());
        }

        let product = this._product.getValue();
        let entitlements = this._entitlements.getValue();

        if (entitlements.baseAccess !== AccessLevel.Write) {
            return of(new SaveApplicationResponse());
        }

        const autoSave = product.allowSaving && entitlements.autoSaveEnabled;
        // manualSaveAllowed: boolean;

        return this.saveApplication({
            formId: this.currentState.formId,
            formVersion: this.currentState.formVersion,
            applicationId: this.currentState.applicationId,
            offlineApplicationId: this.currentState.offlineApplicationId,
            applicationState: this.currentState.workflowState,
            data: toJson(this._applicationData.getValue()),
            publicDidAuthenticate: false,
            publicDidCreateAccount: false,
            isAutoSave: autoSave,
            pageId: this.currentState.pageId,
            subpageId: this.currentState.subPageId,
            created: DateTime.now(),
            modified: DateTime.now(),
            applicationIdDisplay: this.currentState.applicationIdDisplay,
            userId: this._globalService.getCurrentUserId()
        });
    }

    saveApplicationBySearchControl(
        controlName: string,
        searchTerm: string,
        selectedItemIndex: number
    ): Observable<SaveApplicationBySearchControlResponse> {
        return this._applicationServiceProxy.saveApplicationBySearchControl({
            formId: this.currentState.formId,
            formVersion: this.currentState.formVersion,
            applicationId: this.currentState.applicationId,
            controlName: controlName,
            searchTerm: searchTerm,
            selectedItemIndex: selectedItemIndex
        }).pipe(
            tap((result: SaveApplicationBySearchControlResponse) => {
                console.log('FormService.saveApplicationBySearchControl() => SaveBySearchApplicationResult:', result);
                return result;
            }),
            // catchError(this.navigateOnError)
        );
    }

    // Upload a file via the File Handler Service proxy
    // public readonly TEMP_ALIAS = "temporaryId";
    uploadFile(data: FormData): Observable<void | UploadFileResponse> {
        if (this._globalService.isOnline()) {
            return this._formServiceProxy.uploadFile(data);
        } else {
            // ToDo continue if needed
            /*
            if (data == null) {
                return;
            }

            let appId = <string>data.get('applicationId') || <string>data.get('applicationid');
            if (!appId) {
                return;
            }

            let object = {};
            data.forEach((value, key) => {
                object[key] = value;
            });

            let tempId = crypto.randomUUID();
            object[this.TEMP_ALIAS] = tempId;

            return from(this._uploadFileDbProviderService.addUploadFile(object)).pipe(
                switchMap(() => {
                    let result = new UploadFileResponse();
                    result.success = true;
                    result.attachmentId = tempId;
                    result.fileName = object["fileName"] || object["filename"];
                    return of(result);
                })
            );
            */
        }

        // TODO: Implement a local handle on all files uploaded for this application.
        // return this._formServiceProxy.uploadFile(data).pipe(
        //     tap((result: UploadFileResponse) =>
        //     {
        //         console.log('FormService.uploadFile(): ', result);
        //     ...
    }

    getFileDownloadUrl(applicationId: string, attachmentId: string): string {
        if (!applicationId || !attachmentId) {
            return '#';
        }

        return this.fileContentsHandlerUrl +
            '?applicationId=' + applicationId +
            '&fileId=' + attachmentId;
    }

    getControlImageUrl(
        formId: string,
        applicationId: string,
        controlName: string,
        imageId: number,
    ): string {
        return this.fileContentsHandlerUrl +
            `?formId=${formId}&applicationId=${applicationId}&controlName=${controlName}` +
            `&type=controlImage&imageId=${imageId}`;
    }

    readonly navigateOnError = (error) => {
        console.error(error);
        this.destroyApplicationSession();
        this._router.navigateByUrl('/error/400');
        return throwError(() => new Error(error));
    };

    private readonly logErrorOnly = (error) => {
        console.error(error);
        return throwError(() => new Error(error));
    };

    private updateControlEntitlements(product: ProductDefinition, entitlements: EntitlementSet, readonly: boolean) {
        // TODO: REMOVE WHEN UNIT TESTS EXIST!
        product.pages.forEach(page => {
            this.setControlEntitlements(page.controls, entitlements, readonly);
        });
    }

    private setControlEntitlements(controls: iApplyFormControl[], entitlements: EntitlementSet, readonly: boolean) {
        controls.forEach(control => {
            let write = entitlements.w.includes(control.id) ? AccessLevel.Write : null;

            if (readonly) {
                write = write ? AccessLevel.Read : null;
            }

            let read = entitlements.r.includes(control.id) ? AccessLevel.Read : null;
            control.access = write ?? read ?? AccessLevel.NoAccess;

            if (isContainerControl(control)) {
                this.setControlEntitlements(control.controls, entitlements, readonly);
            }
        });
    }

    private setProductClassOverrides(product: ProductDefinition) {
        // Remove class names for all schemes
        this._document.body.classList.remove('full-screen-form');

        if (product) {
            // Full-Screen
            if (product.navigationStyle == NavigationStyle.FullScreen) {
                this._document.body.classList.add('full-screen-form');
            }
        }
    }
}
